import {
	endpointsMap,
	ICreateReturnAuthorizationOptions,
	ICreateReturnAuthorizationResponse,
	IGetReturnAuthorizationReasonsOptions,
	IReturnAuthorizationReasonsResponse,
	IReturnLinkOptions,
	IGetReturnLinkResponse,
	IPrevalidateOptions,
	IPrevalidateResponse,
} from './ICustomShopperReturnAuthorizations';

import AbstractCustomShopperAPI from '../AbstractCustomShopperAPI';
import {ICustomApiConfig} from '../ICustomApiConfig';

/**
 * Represents CustomShopperReturnAuthorizations
 */
class CustomShopperReturnAuthorizations extends AbstractCustomShopperAPI {
	/**
	 * @constructor
	 * @param {ICustomApiConfig} config - API Config
	 */
	constructor(config: ICustomApiConfig) {
		super(config);
	}

	/**
	 * Creates a return authorization request
	 * @param {ICreateReturnAuthorizationOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {ICreateReturnAuthorizationResponse} - URL to redirect after return authorization creation
	 */
	public async createReturnAuthorization(
		fetchOptions: ICreateReturnAuthorizationOptions,
		throwOnError?: boolean
	): Promise<ICreateReturnAuthorizationResponse> {
		const response: ICreateReturnAuthorizationResponse = await this.callCustomEndpoint<ICreateReturnAuthorizationResponse>(
			fetchOptions,
			endpointsMap.CREATE
		);

		if (throwOnError && !response.redirectUrl) {
			const error = new Error(response.detail || 'Return Authorization creation failed.');
			error.name = response.title || 'Return Authorization Error';

			throw error;
		}

		return response;
	}

	/**
	 * pre-validates a return authorization request
	 * @param {IPrevalidateOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IPrevalidateResponse} - URL to redirect after return authorization creation
	 */
	public async preValidateReturnAuthorization(
		fetchOptions: IPrevalidateOptions,
		throwOnError?: boolean
	): Promise<IPrevalidateResponse> {
		const response: IPrevalidateResponse = await this.callCustomEndpoint<IPrevalidateResponse>(
			fetchOptions,
			endpointsMap.PREVALIDATE
		);

		if (throwOnError && response.message !== 'ELIGIBLE_FOR_RETURN') {
			const error = new Error(response.detail || 'Return Authorization prevalidation failed.');
			error.name = response.title || 'Return Authorization Error';

			throw error;
		}

		return response;
	}

	/**
	 * Returns all the possible reasons for Return Authorization
	 * @param {IGetReturnAuthorizationReasonsOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IReturnAuthorizationReasonsResponse} - return authorization search result
	 */
	public async getReturnAuthorizationReasons(
		fetchOptions: IGetReturnAuthorizationReasonsOptions = {},
		throwOnError?: boolean
	): Promise<IReturnAuthorizationReasonsResponse> {
		const response: IReturnAuthorizationReasonsResponse = await this.callCustomEndpoint<IReturnAuthorizationReasonsResponse>(
			fetchOptions,
			endpointsMap.GET_REASONS
		);

		if (throwOnError && !response.returnReasons) {
			throw new Error('Return Authorization search failed.');
		}

		return response;
	}

	/**
	 * Returns the return link for a given order
	 * @param {IReturnLinkOptions} fetchOptions - options to pass to API Call
	 * @param {boolean} throwOnError - throw error if api returns error
	 *
	 * @returns {IGetReturnLinkResponse} - return link
	 */
	public async getReturnLink(
		fetchOptions: IReturnLinkOptions,
		throwOnError: boolean = false
	): Promise<IGetReturnLinkResponse> {
		const response: IGetReturnLinkResponse = await this.callCustomEndpoint<IGetReturnLinkResponse>(
			fetchOptions,
			endpointsMap.GET_RETURN_LINK
		);

		if (throwOnError && !response.returnLink) {
			throw new Error('Return link not found.');
		}

		return response;
	}
}

export default CustomShopperReturnAuthorizations;
